import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Logo from '../components/Logo';
import supabase from '../supabase/supabase';

const { FiUser, FiSettings, FiLogOut, FiChevronDown, FiBell, FiX, FiExternalLink } = FiIcons;

const Header = ({ user, profile, onLogout, onOpenSettings, isAdmin }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const menuRef = useRef(null);
  const notifRef = useRef(null);

  // Use profile data as source of truth for display
  const displayName = profile?.full_name || user?.full_name || user?.email?.split('@')[0] || 'Usuário';
  const displayEmail = profile?.email || user?.email;
  const displayCredits = profile?.credits ?? user?.credits ?? 0;
  const avatarUrl = profile?.avatar_url || user?.avatar_url;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setIsNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch Notifications
  useEffect(() => {
    if (!user) return;
    
    const fetchNotifications = async () => {
      // 1. Get active notifications
      const { data: allNotifs } = await supabase
        .from('app_notifications')
        .select('*')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (!allNotifs) return;

      // 2. Get user interactions
      const { data: reads } = await supabase
        .from('app_notification_reads')
        .select('notification_id, dismissed_at')
        .eq('user_id', user.id);

      const dismissedIds = new Set(reads?.filter(r => r.dismissed_at).map(r => r.notification_id));
      
      // Filter out dismissed notifications
      const activeNotifs = allNotifs.filter(n => !dismissedIds.has(n.id));
      setNotifications(activeNotifs);

      // Check unread count (not viewed yet)
      // We'll consider "unread" as not in the reads table at all or viewed_at is null
      const readIds = new Set(reads?.map(r => r.notification_id));
      const unread = activeNotifs.filter(n => !readIds.has(n.id)).length;
      setUnreadCount(unread);
    };

    fetchNotifications();
    // Refresh every 2 minutes
    const interval = setInterval(fetchNotifications, 120000);
    return () => clearInterval(interval);
  }, [user]);

  const handleOpenNotifs = async () => {
    setIsNotifOpen(!isNotifOpen);
    if (!isNotifOpen && unreadCount > 0) {
      // Mark displayed notifications as viewed
      const unreadNotifs = notifications.filter(n => true); // logic simplified, we mark all current as viewed
      
      for (const notif of unreadNotifs) {
        await supabase.from('app_notification_reads').upsert({
          user_id: user.id,
          notification_id: notif.id,
          viewed_at: new Date().toISOString()
        }, { onConflict: 'notification_id, user_id' });
      }
      setUnreadCount(0);
    }
  };

  const handleDismiss = async (e, id) => {
    e.stopPropagation();
    setNotifications(prev => prev.filter(n => n.id !== id));
    await supabase.from('app_notification_reads').upsert({
      user_id: user.id,
      notification_id: id,
      dismissed_at: new Date().toISOString()
    }, { onConflict: 'notification_id, user_id' });
  };

  const handleClickNotif = async (notif) => {
    if (notif.action_url) {
      window.open(notif.action_url, '_blank');
      await supabase.from('app_notification_reads').upsert({
        user_id: user.id,
        notification_id: notif.id,
        clicked_at: new Date().toISOString()
      }, { onConflict: 'notification_id, user_id' });
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between">
        <Logo />

        <div className="flex items-center gap-4">
          {/* Notifications Bell */}
          <div className="relative" ref={notifRef}>
            <button 
              onClick={handleOpenNotifs}
              className={`relative p-3 rounded-2xl transition-all ${isNotifOpen ? 'bg-indigo-50 text-indigo-600' : 'text-slate-400 hover:text-indigo-600 hover:bg-indigo-50'}`}
            >
              <SafeIcon icon={FiBell} className="text-xl" />
              {unreadCount > 0 && (
                <span className="absolute top-3 right-3 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
              )}
            </button>

            <AnimatePresence>
              {isNotifOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute right-0 mt-3 w-80 md:w-96 bg-white rounded-[2rem] shadow-2xl shadow-slate-200/50 border border-slate-100 overflow-hidden"
                >
                  <div className="px-6 py-4 border-b border-slate-50 bg-slate-50/50">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Notificações</p>
                  </div>
                  
                  <div className="max-h-[400px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center">
                        <SafeIcon icon={FiBell} className="text-3xl text-slate-100 mx-auto mb-2" />
                        <p className="text-xs font-bold text-slate-300 uppercase">Tudo limpo por aqui</p>
                      </div>
                    ) : (
                      notifications.map(notif => (
                        <div 
                          key={notif.id}
                          onClick={() => handleClickNotif(notif)}
                          className={`p-5 border-b border-slate-50 hover:bg-indigo-50/30 transition-colors relative group cursor-pointer`}
                        >
                          <div className="pr-6">
                            <h4 className="text-sm font-bold text-slate-800 mb-1 leading-tight">{notif.title}</h4>
                            <p className="text-xs text-slate-500 leading-relaxed">{notif.body}</p>
                            {notif.action_url && (
                              <div className="mt-2 flex items-center gap-1 text-[10px] font-black text-indigo-500 uppercase tracking-wide">
                                Saiba mais <SafeIcon icon={FiExternalLink} />
                              </div>
                            )}
                          </div>
                          <button 
                            onClick={(e) => handleDismiss(e, notif.id)}
                            className="absolute top-4 right-4 text-slate-300 hover:text-red-400 p-1 rounded-full hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100"
                          >
                            <SafeIcon icon={FiX} className="text-lg" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* User Menu */}
          <div className="relative" ref={menuRef}>
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="flex items-center gap-3 p-1.5 pl-4 bg-slate-50 border border-slate-100 rounded-2xl hover:bg-slate-100 transition-all group"
            >
              <div className="hidden md:flex flex-col items-end mr-1">
                <span className="text-xs font-black text-slate-800 uppercase italic leading-none truncate max-w-[120px]">
                  {displayName.split(' ')[0]}
                </span>
                <span className={`text-[9px] font-bold uppercase tracking-tighter mt-1 ${isAdmin ? 'text-amber-600' : 'text-slate-400'}`}>
                  {isAdmin ? 'Administrador' : `${displayCredits} Créditos`}
                </span>
              </div>
              <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white text-sm font-black shadow-lg shadow-indigo-100 overflow-hidden">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  displayName.charAt(0).toUpperCase()
                )}
              </div>
              <SafeIcon icon={FiChevronDown} className={`text-slate-400 transition-transform duration-300 ${isMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {isMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute right-0 mt-3 w-64 bg-white rounded-[2rem] shadow-2xl shadow-slate-200/50 border border-slate-100 p-3"
                >
                  <div className="px-5 py-4 border-b border-slate-50 mb-2">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Logado como</p>
                    <p className="text-sm font-black text-slate-900 truncate">{displayEmail}</p>
                  </div>

                  <button 
                    onClick={() => {
                      setIsMenuOpen(false);
                      onOpenSettings();
                    }}
                    className="w-full flex items-center gap-3 px-5 py-3.5 text-slate-600 font-bold text-xs hover:bg-indigo-50 hover:text-indigo-600 rounded-xl transition-all group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center group-hover:bg-indigo-100 group-hover:text-indigo-600 transition-all">
                      <SafeIcon icon={FiSettings} />
                    </div>
                    Configurações
                  </button>

                  <button 
                    onClick={() => {
                      setIsMenuOpen(false);
                      onLogout();
                    }}
                    className="w-full flex items-center gap-3 px-5 py-3.5 text-red-500 font-bold text-xs hover:bg-red-50 rounded-xl transition-all group mt-1"
                  >
                    <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-all">
                      <SafeIcon icon={FiLogOut} />
                    </div>
                    Sair da Conta
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;